import * as stylex from "@stylexjs/stylex";
import "../tokens.stylex.ts";
import { color, motion, shadow, texture } from "../tokens.stylex.ts";

/**
 * Raised surface — the signature look of the product.
 *
 * A *face* (dot-grid texture fading in toward the bottom, hairline border)
 * rests on a *slab*: a hatched copy of the same shape, offset down-left, that
 * casts the soft drop shadow. Both are pseudo-elements painted behind the
 * host's content inside its own stacking context, so the recipe works on any
 * element without extra markup:
 *
 *   stylex.props(surface.raised, surface.md, styles.myLayout)
 *
 * The host keeps a transparent 1px border so layout is identical to a normal
 * bordered box. Never put `overflow: hidden` on a raised host (it would clip
 * the slab); clip an inner element instead.
 *
 * Per-instance knobs are CSS custom properties, so variants and pseudo-classes
 * (`:hover`, `:active`) can retune the face without touching the pseudo
 * elements: `--face-bg`, `--face-border`, `--face-dot`, `--slab`,
 * `--slab-bg`, `--slab-hatch`, `--slab-shadow`.
 */
export const surface = {
  raised: {
    "--face-bg": "x1upr4va",
    "--face-border": "x9lrtta",
    "--face-dot": "x1f3ljd4",
    "--slab": "xm9xnzn",
    "--slab-bg": "x4mgtff",
    "--slab-border": "xoudbfq",
    "--slab-hatch": "x100daw0",
    "--slab-shadow": "xte3f84",
    kVAEAm: "x1n2onr6",
    kHBbk8: "xc8icb0",
    kWkggS: "xjbqb8w",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x9r1u3d",
    kgeoSG: "x1cpjm7i",
    kEoFBp: "x1hmns74",
    kFcpXp: "x1h5trzc",
    kxmVGU: "xy5mcqj",
    kPB484: "xxyica1",
    k5Ohv7: "xz8fyzm",
    kj4x2v: "x1fo1fc9",
    kfXFYZ: "xdt36v5",
    kLkRvE: "x120kfy0",
    k3DiCg: "x14m7zh9",
    ktNmms: "x1kspj7d",
    kcTAPf: "x15tfxy0",
    k7KCAZ: "x1hu8v7x",
    kKXxxB: "xmt6lav",
    kqUdNP: "xi5owl6",
    km8f2m: "xkk1bqk",
    k5JduY: "x1s928wv",
    kwXMNM: "x1j6awrg",
    kv0HGH: "xgmhms5",
    kDVEDD: "xi4xitw",
    kUfP38: "x1k48kgn",
    kPGmMm: "x5hscyn",
    kbwMcY: "x86avzo",
    kXtpa: "x80r2qw",
    ks3ayO: "x1f8ta0v",
    kNctxI: "x1x5amxz",
    kakxe6: "xogy3md",
    kRicXK: "xgzs4c1",
    kPNhGg: "x13uqbgb",
    kloYau: "x2q1x1w",
    $$css: true
  },
  xs: {
    "--slab": "x3ges0i",
    "--slab-shadow": "xxlq3h8",
    $$css: true
  },
  sm: {
    "--slab": "xbq946m",
    "--slab-shadow": "x1dbxqk1",
    $$css: true
  },
  md: {
    "--slab": "xm9xnzn",
    "--slab-shadow": "xte3f84",
    $$css: true
  },
  lg: {
    "--slab": "x1bxo5bq",
    "--slab-shadow": "xvk5ck5",
    $$css: true
  },
  plain: {
    "--face-dot": "x1y7s6vt",
    $$css: true
  },
  accent: {
    "--face-bg": "x748x8c",
    "--face-border": "x1rfjqpn",
    "--face-dot": "x1tfdxok",
    "--slab-bg": "x66hgcj",
    "--slab-border": "xoom245",
    "--slab-hatch": "x1qt4fxi",
    $$css: true
  },
  inverse: {
    "--face-bg": "xgpig4l",
    "--face-border": "xaimbxu",
    "--face-dot": "x1tfdxok",
    "--slab-bg": "xu3n1fg",
    "--slab-border": "x135sdc7",
    "--slab-hatch": "x1qt4fxi",
    $$css: true
  },
  hatched: {
    kWkggS: "xguwle4",
    kKwaWg: "xz7ykf5",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x12yr27d",
    kGVxlE: "xwgq1kd",
    $$css: true
  },
  focusable: {
    kMeerF: "x1de99jn",
    k3XXqK: "x1t137rt x9v5kkp",
    kjBf7l: "x15xi569",
    kInvED: "x1hl8ikr",
    $$css: true
  }
};